import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import { AutorenewOutlined, CalendarMonthOutlined, CardGiftcardOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAccountData, useStorefrontData } from '../StorefrontThemeWrapper';
import { BookingWidgetWrapper } from '../experiencePage/components/BookingWidgetWrapper';
import { StorefrontHeader } from '../common/StorefrontHeader';
import { StorefrontFooter } from '../common/StorefrontFooter';
import { AboutSection } from '../storefrontPage/aboutSection/AboutSection';
import { AccountLogo } from '../../shared/ui/AccountLogo';
import { getGiftCardOrderPage } from '../../utils/AccountUtils';
import { useLanguage } from '../../utils/GeneralUtils';
import { customerFacingPriceFormatter } from '../../orderPage/utils';
import { AccountData } from '../../api';
import { GiftCardAmountContext } from './GiftCardAmountContext';

const FEATURE_ICONS = [CardGiftcardOutlined, CalendarMonthOutlined, AutorenewOutlined];
const ITEM_INDEXES = [0, 1, 2];

const GiftCardUnavailable = () => {
  const { t } = useTranslation();
  const { accountId, storefrontId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid
      container
      flexDirection="column"
      sx={{ maxWidth: 1300, margin: '0 auto', padding: isSmallScreen ? '0 16px' : '0 80px' }}>
      <StorefrontHeader />
      <Grid container flexDirection="column" alignItems="center" gap={2} py={10}>
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
          {t('storefront.giftCardUnavailable')}
        </Typography>
        {storefrontId && (
          <Button
            component={Link}
            to={{ pathname: `/storefront/${accountId}/${storefrontId}`, search: searchParams.toString() }}
            variant="outlined">
            {t('storefront.backToSite')}
          </Button>
        )}
      </Grid>
      <StorefrontFooter />
    </Grid>
  );
};

type GiftCardMobileBarProps = {
  onBuyClick: () => void;
};

const GiftCardMobileBar = ({ onBuyClick }: GiftCardMobileBarProps) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderTop: '1px solid #e0e0e0',
        boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'center',
        paddingX: 2,
        paddingY: 1.5,
      }}>
      <Button variant="contained" onClick={onBuyClick} sx={{ paddingY: 1.5, paddingX: 4, fontWeight: 600 }}>
        {t('storefront.giftCardCta')}
      </Button>
    </Box>
  );
};

type GiftCardVisualProps = {
  account: AccountData;
  mainColor: string;
  amount: number;
  cardTitle: string;
  cardSubtitle: string;
};

const GiftCardVisual = ({ account, mainColor, amount, cardTitle, cardSubtitle }: GiftCardVisualProps) => {
  const { t } = useTranslation();
  const decorativeCircleSx = { position: 'absolute', borderRadius: '50%', backgroundColor: alpha('#ffffff', 0.1) };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: 470,
        height: 290,
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        transform: 'rotate(-3deg)',
        backgroundColor: 'white',
      }}>
      <Box
        sx={{
          flex: '0 0 60%',
          minWidth: 0,
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
        <AccountLogo account={account} width={240} style={{ maxHeight: 64, maxWidth: '100%', objectFit: 'contain' }} />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 26, lineHeight: 1.2 }}>{cardTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
            {cardSubtitle}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          flex: '0 0 40%',
          minWidth: 0,
          backgroundColor: mainColor,
          color: 'white',
          padding: 2.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'center',
        }}>
        <Box sx={{ ...decorativeCircleSx, width: 160, height: 160, insetBlockStart: -50, insetInlineEnd: -60 }} />
        <Box sx={{ ...decorativeCircleSx, width: 120, height: 120, insetBlockEnd: -40, insetInlineStart: -40 }} />
        <Typography variant="caption" sx={{ position: 'relative', opacity: 0.9 }}>
          {t('giftCardPage.cardValueLabel')}
        </Typography>
        <Typography sx={{ position: 'relative', fontWeight: 800, fontSize: 40, lineHeight: 1 }}>
          {customerFacingPriceFormatter(amount, account)}
        </Typography>
        <Typography variant="caption" sx={{ position: 'relative', opacity: 0.9 }}>
          {account.name}
        </Typography>
      </Box>
    </Box>
  );
};

type GiftCardContentProps = {
  account: AccountData;
  giftCardOrderPageId: string;
};

const GiftCardContent = ({ account, giftCardOrderPageId }: GiftCardContentProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const storefront = useStorefrontData();
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>();

  useEffect(() => {
    const widgetElement = widgetRef.current;
    if (!widgetElement) {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setIsWidgetVisible(entry.isIntersecting), {
      threshold: 0.1,
    });
    observer.observe(widgetElement);
    return () => observer.disconnect();
  }, []);

  const mainColor = storefront?.styling?.button_color || '#16854C';
  const description = account.monetary_voucher_settings?.page_description?.[language];
  const presetAmounts = account.monetary_voucher_settings?.preset_amounts ?? [];
  const displayAmount = selectedAmount || Math.max(0, ...presetAmounts);

  const features = ITEM_INDEXES.map((index) => ({
    title: t(`giftCardPage.features.${index}.title`),
    text: t(`giftCardPage.features.${index}.text`),
  }));
  const steps = ITEM_INDEXES.map((index) => ({
    title: t(`giftCardPage.steps.${index}.title`),
    text: t(`giftCardPage.steps.${index}.text`),
  }));

  const scrollToWidget = () => {
    widgetRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ paddingBottom: isSmallScreen ? '90px' : 0 }}>
      <Box sx={{ maxWidth: 1300, margin: '0 auto', padding: isSmallScreen ? '0 16px' : '0 80px' }}>
        <StorefrontHeader />
      </Box>

      <GiftCardAmountContext.Provider value={setSelectedAmount}>
        <Box sx={{ backgroundColor: lighten(mainColor, 0.92), paddingBlock: isSmallScreen ? 3 : 8 }}>
          <Box
            sx={{
              maxWidth: 1140,
              margin: '0 auto',
              paddingInline: isSmallScreen ? 2 : 3,
              display: 'grid',
              gridTemplateColumns: isDesktop ? 'minmax(0, 1fr) 420px' : 'minmax(0, 1fr)',
              columnGap: 8,
              rowGap: isSmallScreen ? 3 : 5,
              alignItems: 'start',
            }}>
            <Box sx={{ gridColumn: 1, gridRow: isDesktop ? 1 : 'auto' }}>
              <Chip
                label={t('giftCardPage.eyebrow')}
                sx={{
                  backgroundColor: lighten(mainColor, 0.8),
                  color: darken(mainColor, 0.35),
                  fontWeight: 700,
                  fontSize: 13,
                  marginBottom: 2,
                }}
              />
              <Typography
                variant="h1"
                sx={{ fontWeight: 800, fontSize: isSmallScreen ? 32 : 48, lineHeight: 1.15, marginBottom: 2 }}>
                {t('giftCardPage.title')}
              </Typography>
              {description && (
                <Typography sx={{ fontSize: 18, lineHeight: 1.8, maxWidth: 560 }} color="text.secondary">
                  {description}
                </Typography>
              )}
            </Box>

            <Box
              ref={widgetRef}
              sx={{
                gridColumn: isDesktop ? 2 : 1,
                gridRow: isDesktop ? '1 / span 3' : 'auto',
                minWidth: 0,
              }}>
              <BookingWidgetWrapper
                storefrontStyling={storefront?.styling}
                orderPageIdOverride={giftCardOrderPageId}
                startsMonetaryGiftCard
              />
            </Box>

            <Box sx={{ gridColumn: 1, gridRow: isDesktop ? 2 : 'auto', paddingBlock: 2, minWidth: 0 }}>
              <GiftCardVisual
                account={account}
                mainColor={mainColor}
                amount={displayAmount}
                cardTitle={t('giftCardPage.cardTitle')}
                cardSubtitle={t('giftCardPage.cardSubtitle')}
              />
            </Box>

            <Box
              sx={{
                gridColumn: 1,
                gridRow: isDesktop ? 3 : 'auto',
                display: 'grid',
                gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                gap: 2,
              }}>
              {features.map((feature, index) => {
                const FeatureIcon = FEATURE_ICONS[index];
                return (
                  <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: darken(mainColor, 0.2),
                      }}>
                      <FeatureIcon />
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{feature.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </GiftCardAmountContext.Provider>

      <Box
        sx={{
          maxWidth: 1140,
          margin: '0 auto',
          paddingInline: isSmallScreen ? 2 : 3,
          paddingBlock: isSmallScreen ? 6 : 10,
        }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            fontSize: isSmallScreen ? 28 : 36,
            textAlign: 'center',
            marginBottom: isSmallScreen ? 3 : 5,
          }}>
          {t('giftCardPage.howItWorksTitle')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: 3,
          }}>
          {steps.map((step, index) => (
            <Box key={index} sx={{ backgroundColor: theme.palette.grey[100], borderRadius: '20px', padding: 4 }}>
              <Typography
                sx={{ fontWeight: 800, fontSize: 44, lineHeight: 1, color: lighten(mainColor, 0.35), marginBottom: 2 }}>
                {index + 1}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 20, marginBottom: 1 }}>{step.title}</Typography>
              <Typography color="text.secondary">{step.text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1300, margin: '0 auto', padding: isSmallScreen ? '0 16px' : '0 80px' }}>
        <Grid container>
          <AboutSection businessDetails={account.business_details} accountName={account.label?.[language] || ''} />
        </Grid>
        <StorefrontFooter businessDetails={account.business_details} />
      </Box>

      {isSmallScreen && !isWidgetVisible && <GiftCardMobileBar onBuyClick={scrollToWidget} />}
    </Box>
  );
};

export const GiftCardPage = () => {
  const account = useAccountData();
  const giftCardOrderPage = getGiftCardOrderPage(account);

  if (!account) {
    return null;
  }

  if (!giftCardOrderPage) {
    return <GiftCardUnavailable />;
  }

  return <GiftCardContent account={account} giftCardOrderPageId={giftCardOrderPage.id} />;
};
